export interface BasicRange<T> {
    readonly start: T;
    readonly end: T;
}

export function transform<T, U>(
    range: BasicRange<T>,
    transformFn: (t: T) => U
): BasicRange<U> {
    return {
        start: transformFn(range.start),
        end: transformFn(range.end),
    };
}

export abstract class ComparableRange<
    PointType,
    RangeType,
    CreationParams = null
> implements BasicRange<PointType>
{
    protected constructor(
        public readonly start: PointType,
        public readonly end: PointType
    ) {
        if (this.compare(start, end) > 0) {
            throw new Error('invalid range');
        }
    }

    protected abstract create(
        t1: PointType,
        t2: PointType,
        other: CreationParams
    ): RangeType;
    protected abstract compare(t1: PointType, t2: PointType): number;

    protected getCreationParams(): CreationParams {
        return null;
    }

    private minOf(t1: PointType, t2: PointType): PointType {
        return this.compare(t1, t2) < 0 ? t1 : t2;
    }

    private maxOf(t1: PointType, t2: PointType): PointType {
        return this.compare(t1, t2) > 0 ? t1 : t2;
    }

    public transform(transformFn: (t: PointType) => PointType): RangeType {
        const transformed = transform(this, transformFn);
        return this.create(
            transformed.start,
            transformed.end,
            this.getCreationParams()
        );
    }

    public union(other: BasicRange<PointType>): RangeType {
        return this.create(
            this.minOf(this.start, other.start),
            this.maxOf(this.end, other.end),
            this.getCreationParams()
        );
    }

    public intersect(other: BasicRange<PointType>): RangeType {
        return this.create(
            this.maxOf(this.start, other.start),
            this.minOf(this.end, other.end),
            this.getCreationParams()
        );
    }

    public limitStart(other: PointType): RangeType {
        return this.create(
            this.maxOf(this.start, other),
            this.end,
            this.getCreationParams()
        );
    }

    public limitEnd(other: PointType): RangeType {
        return this.create(
            this.start,
            this.minOf(this.end, other),
            this.getCreationParams()
        );
    }

    public withStart(start: PointType): RangeType {
        return this.create(start, this.end, this.getCreationParams());
    }

    public withEnd(end: PointType): RangeType {
        return this.create(this.start, end, this.getCreationParams());
    }

    public contains(point: PointType): boolean {
        return (
            this.compare(this.start, point) <= 0 &&
            this.compare(this.end, point) > 0
        );
    }
}

export class NumberRange extends ComparableRange<number, NumberRange> {
    public static from(range: BasicRange<number>): NumberRange {
        return new NumberRange(range.start, range.end);
    }

    public constructor(start: number, end: number) {
        super(start, end);
    }

    protected compare(t1: number, t2: number): number {
        return t1 - t2;
    }

    protected create(t1: number, t2: number): NumberRange {
        return new NumberRange(t1, t2);
    }
}
